import { cn } from '@/lib/utils';

/**
 * The one row every tab opens with.
 *
 * THERE IS NO APP BAR IN THIS PRODUCT, and that is a decision rather than an
 * omission. A sticky title bar puts the actions people use most at the top of
 * a phone, which is the hardest place on the screen to reach, and it spends a
 * whole row of vertical space restating the tab you are already standing in —
 * the tab bar at the bottom has said it, in a place the thumb can get to.
 *
 * What replaced it is this: a tab that needs a control begins with exactly one
 * row, of the same height, holding the one thing that tab is for. The shapes
 * rhyme on purpose.
 *
 *   Feed   [ ⌕ Search spaces………………………………………………… ]
 *   Loft   [ ⌕ Search your correspondence………………………… ]
 *   You    [avatar] [ Yasmin · @yasmin_lately ]    [theme]
 *
 * THE FEED HAS ONE NOW, AND A FIELD IS ALL IT HAS EVER BEEN GIVEN. This said "the
 * Feed has no row and wants none" for as long as the only candidate was a composer
 * prompt — which is the create button in the middle of the tab bar
 * (`CreateButton`) — and the rule that argument came from is unchanged: a row here
 * must be a CONTROL and never a title. A field that finds rooms is a control
 * (docs/5 § D260803-1933), and it searches rooms rather than people, which is the whole of
 * why it is allowed to exist.
 *
 * THE LOFT'S ROW HAS NO TRAILING ACTION, and that is why the diagram above is
 * lopsided. It ended in a pen that opened the composer's Letter tab, which the
 * plus in the bar opens from this screen too (docs/5 § D260803-1928) — so the field takes
 * the width the button was in. A trailing action is what a row has when the tab
 * needs one, not a slot every row has to fill.
 *
 * The height is set by a 40px leading element — a search field on two of them, an
 * avatar on the third — so the rows measure identically without anyone
 * hard-coding a height. Callers must keep that: a smaller avatar or a shorter
 * input here breaks the rhyme, and the rhyme is the whole point.
 *
 * Not sticky. The Loft's rows are tall enough that only about three fit on a
 * phone, and a pinned bar would permanently spend a third of what you can see
 * on a control used once a session.
 */
export function TopRow({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="top-row"
      /* NO RULE UNDER IT. The row is already legible as a row: 40px of control
         running its full width. A border there was drawing the bottom edge of an
         app bar that this component exists to not be, and it was the line that
         made the Feed open on a box. */
      className={cn('flex items-center gap-3 px-4 py-3', className)}
      {...props}
    />
  );
}

/**
 * The trailing icon action a top row ends with.
 *
 * 40px, the same as the avatar and the search field beside it — so the three
 * tabs' opening rows measure identically without a hard-coded height, and so
 * the target is one a thumb can actually hit. The button primitive's own
 * `icon` sizes top out at 36px, which is why this overrides rather than
 * picking one of them.
 */
export const TOP_ROW_ACTION =
  'size-10 shrink-0 rounded-full text-muted-foreground';
